import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Phone, Mail, Download, Linkedin } from "lucide-react";

export function ApplicationsModule() {
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/admin/applications"), api.get("/admin/jobs")])
      .then(([a, j]) => { setItems(a.data); setJobs(j.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => (jobFilter === "All" ? items : items.filter((i) => i.job_id === jobFilter)),
    [items, jobFilter]
  );

  const remove = async (item) => {
    if (!window.confirm(`Delete application from "${item.name}"?`)) return;
    try {
      await api.delete(`/admin/applications/${item.id}`);
      toast.success("Application deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-module-applications">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Job Applications</h2>
          <p className="text-sm text-slate-400">{items.length} application{items.length === 1 ? "" : "s"}</p>
        </div>
        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger data-testid="applications-filter" className="bg-[#0D111D] border-white/10 text-slate-200 w-56">
            <SelectValue placeholder="Filter by job" />
          </SelectTrigger>
          <SelectContent className="bg-[#0D111D] border-white/10 text-slate-200">
            <SelectItem value="All">All jobs</SelectItem>
            {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400" data-testid="applications-empty">No applications yet.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <div key={item.id} data-testid={`application-row-${item.id}`} className="glass rounded-xl p-5 flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <p className="font-display font-semibold text-white">{item.name}</p>
                  <span className="text-[10px] font-mono-tech px-2 py-0.5 rounded-full border border-[#FF8800]/40 text-[#FF8800]">
                    {item.job_title}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
                  <a href={`tel:${(item.phone || "").replace(/[^+\d]/g, "")}`} className="flex items-center gap-1.5 hover:text-[#00E676] transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {item.phone}
                  </a>
                  <a href={`mailto:${item.email}`} className="flex items-center gap-1.5 hover:text-[#00B0FF] transition-colors">
                    <Mail className="w-3.5 h-3.5" /> {item.email}
                  </a>
                  {item.linkedin && (
                    <a href={item.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#00B0FF] transition-colors">
                      <Linkedin className="w-3.5 h-3.5" /> LinkedIn
                    </a>
                  )}
                  {item.experience && <span className="text-slate-500">· {item.experience}</span>}
                </div>
                {item.cover_message && <p className="mt-2.5 text-sm text-slate-300 leading-relaxed line-clamp-3">{item.cover_message}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {item.resume && (
                  <a href={item.resume} download={`resume-${item.name.replace(/\s+/g, "-")}`} data-testid={`application-resume-${item.id}`}>
                    <Button size="sm" variant="outline" className="border-white/15 text-slate-300 hover:bg-white/5">
                      <Download className="w-4 h-4 mr-1.5" /> Resume
                    </Button>
                  </a>
                )}
                <Button size="icon" variant="outline" data-testid={`application-delete-${item.id}`} onClick={() => remove(item)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
